import { Modal, Result } from 'antd';

import Button from '../components/elements/Button';
import { BASE_API_URL } from '../config';
import { getUserFriendlyErrorMessage } from '../util/errorMessages';
import AuthService from './auth';

export enum AuthErrorType {
  INVALID_TOKEN = 'INVALID_TOKEN',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  OTHER = 'OTHER',
}

export interface ApiResponse {
  data: any;
  notifications?: string[];
  pagination?: PaginationModel;
}
export interface ListApiResponse {
  data: any;
  pagination: PaginationModel;
  meta?: any;
}

export interface DeleteApiResponse {
  notifications: string[];
}

export interface ApiError {
  error: {
    code: number;
    type?: AuthErrorType;
    message: string;
  };
}

export interface PaginationModel {
  offset: number;
  limit: number;
  count: number;
  total?: number;
}

export const DEFAULT_ERR_MSG =
  'Oops! Something went wrong. Please refresh and try again.';

const isProductionEnvironment = () => {
  const baseApiUrl = BASE_API_URL();
  return baseApiUrl.includes('prod');
};

const errorResult = (err: ApiError) => {
  const userFriendlyMessage = getUserFriendlyErrorMessage(err.error.message);

  const isAuthLoginError =
    err.error.code === 105 ||
    err.error.message === 'Incorrect email and/or password';

  let errorMessage = isProductionEnvironment()
    ? DEFAULT_ERR_MSG
    : userFriendlyMessage;

  if (isAuthLoginError) {
    errorMessage = userFriendlyMessage;
  }

  if (
    err.error.code === 111 &&
    err.error.message.includes('Missing resource')
  ) {
    errorMessage = isProductionEnvironment()
      ? DEFAULT_ERR_MSG
      : err.error.message;
  }

  const buttonAction = () => {
    if (isAuthLoginError) {
      Modal.destroyAll();
      return;
    }

    if (
      isProductionEnvironment() ||
      (err.error.code === 111 && err.error.message.includes('Missing resource'))
    ) {
      if (localStorage.getItem('token')) {
        console.error(
          '[AUTH] error: Redirecting to dashboard with token in localStorage'
        );
      }
      const ENV = window.location.origin;
      window.location.replace(`${ENV}`);
    } else {
      Modal.destroyAll();
    }
  };
  const buttonLabel =
    isAuthLoginError ||
    (!isProductionEnvironment() &&
      err.error.code !== 111 &&
      !err.error.message.includes('Missing resource'))
      ? 'I understand'
      : 'Back to dashboard';

  return (
    <Result
      status="error"
      title={errorMessage}
      extra={[
        <Button
          key="error-modal-button"
          type="primary"
          onClick={buttonAction}
          style={{ width: '100%' }}
          autoFocus
        >
          {buttonLabel}
        </Button>,
      ]}
    />
  );
};

export const handleError = (err: ApiError): void => {
  // Check for all authentication-related errors that should trigger logout
  const isAuthError =
    err?.error?.type === AuthErrorType.INVALID_TOKEN ||
    err?.error?.type === AuthErrorType.TOKEN_EXPIRED ||
    err?.error?.type === AuthErrorType.UNAUTHORIZED ||
    err?.error?.code === 401 ||
    (err?.error?.message &&
      (err.error.message.includes('Invalid authentication') ||
        err.error.message.includes('Unauthorized') ||
        err.error.message.includes('Missing Authorization Token') ||
        err.error.message.includes('Invalid Authorization Token')));

  if (isAuthError) {
    AuthService().logout();
    return;
  }

  const hasToken = !!localStorage.getItem('token');
  if (hasToken) {
    console.error(
      '[AUTH] error: Non-auth error with token present',
      '| Code:',
      err?.error?.code,
      '| Type:',
      err?.error?.type,
      '| Will show dismissible modal'
    );
  }

  const modal = Modal.error({
    icon: null,
    className: 'global-error-modal',
    maskClosable: true,
    onCancel: () => {
      if (localStorage.getItem('token')) {
        console.error(
          '[AUTH] error: Error modal dismissed, token still in localStorage'
        );
      }
      Modal.destroyAll();
    },
    centered: true,
    keyboard: true,
    content: errorResult(err),
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (localStorage.getItem('token')) {
        console.error(
          '[AUTH] error: Error modal dismissed (Enter), token still in localStorage'
        );
      }
      Modal.destroyAll();
    }
  };

  setTimeout(() => {
    document.addEventListener('keydown', handleKeyDown);

    const originalDestroy = modal.destroy;
    modal.destroy = () => {
      if (localStorage.getItem('token')) {
        console.error(
          '[AUTH] error: Error modal destroyed, token still in localStorage'
        );
      }
      document.removeEventListener('keydown', handleKeyDown);
      originalDestroy();
    };
  }, 100);
};

export const postAuth = (url: string, data: any): Promise<ApiResponse> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = window.localStorage.getItem('token');
  if (token) {
    headers.Authorization = token;
  }

  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    })
      .then((resp) => resp.json())
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const post = (url: string, data: any): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${window.localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const postFile = (url: string, fileData: FormData): Promise<any> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `${window.localStorage.getItem('token')}`,
      },
      body: fileData,
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const copy = (url: string, data: any): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'COPY',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${window.localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const put = (url: string, data: any): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${window.localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const patch = (url: string, data: any): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${window.localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const get = (url: string): Promise<ApiResponse | ListApiResponse> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${window.localStorage.getItem('token')}`,
      },
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const del = (url: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${window.localStorage.getItem('token')}`,
      },
    })
      .then((resp) => {
        if (resp.status === 401) {
          throw {
            error: {
              code: 401,
              type: AuthErrorType.UNAUTHORIZED,
              message: 'Unauthorized request',
            },
          };
        }
        return resp.json();
      })
      .then((payload) => {
        if (payload.error) return reject(payload);
        resolve(payload);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
